
import { supabase } from './client';
import { DEFAULT_TIMEOUTS, NetworkStatus, retryQueue, calculateBackoff } from '@/utils/networkUtils';
import { PostgrestError } from '@supabase/supabase-js';
import { RpcFunctionName, RpcResponse } from './rpc-types';
import { typedRpc } from './typedRpcClient';

/**
 * Enhanced version of the Supabase client with standardized timeout and retry logic
 */
export const enhancedSupabase = {
  /**
   * Perform a database query with standardized timeout and retry handling
   */
  async query<T = any>(
    queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>,
    options: {
      timeout?: number;
      retries?: number;
      retryableErrors?: string[];
      critical?: boolean;
      cacheKey?: string;
    } = {}
  ): Promise<{ data: T | null; error: PostgrestError | null; source: 'network' | 'cache' }> {
    const {
      timeout = DEFAULT_TIMEOUTS.STANDARD,
      retries = 3,
      retryableErrors = ['20', '408', 'ETIMEDOUT', 'ECONNREFUSED', 'network error'],
      critical = false,
      cacheKey
    } = options;

    // Try to get from cache if we have a cache key
    if (cacheKey) {
      const cached = sessionStorage.getItem(`supabase_cache_${cacheKey}`);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          // Use cache if it's less than 5 minutes old
          if (cacheAge < 5 * 60 * 1000) {
            console.log(`Using cached data for "${cacheKey}"`);
            return { data, error: null, source: 'cache' };
          }
        } catch (e) {
          console.warn('Error parsing cached data:', e);
        }
      }
    }

    // Network is offline, queue critical requests for later
    if (!NetworkStatus.isOnline() && critical) {
      console.log('Network is offline, queuing critical request for later');
      
      // Add to retry queue
      retryQueue.add(async () => {
        const result = await this.query(queryFn, options);
        console.log('Completed queued request:', result);
        return result;
      });
      
      return { 
        data: null, 
        error: { message: 'Network is offline', code: 'OFFLINE' } as PostgrestError, 
        source: 'network' 
      };
    }

    // Perform the query with timeouts and retries
    let attempt = 0;
    
    while (attempt <= retries) {
      try {
        // Attach timeout to the query
        const wrappedQuery = async () => {
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error(`Query timeout after ${timeout}ms`)), timeout);
          });

          return Promise.race([
            queryFn(),
            timeoutPromise
          ]);
        };

        const result = await wrappedQuery();
        
        // Cache successful results if we have a cache key
        if (cacheKey && result.data && !result.error) {
          try {
            sessionStorage.setItem(`supabase_cache_${cacheKey}`, JSON.stringify({
              data: result.data,
              timestamp: Date.now()
            }));
          } catch (e) {
            console.warn('Error caching data:', e);
          }
        }
        
        return { ...result, source: 'network' };
      } catch (error: any) {
        const errorMessage = error.message || '';
        const errorCode = error.code || '';
        
        // Check if this is a retryable error
        const isRetryable = retryableErrors.some(code => 
          errorMessage.includes(code) || errorCode.includes(code)
        );
        
        if (!isRetryable || attempt >= retries) {
          console.error(`Query failed after ${attempt + 1} attempts:`, error);
          return { 
            data: null, 
            error: { 
              message: errorMessage || 'Network connection error', 
              code: errorCode || 'NETWORK_ERROR' 
            } as PostgrestError,
            source: 'network'
          };
        }
        
        // Calculate backoff time
        const backoffTime = calculateBackoff(attempt);
        console.log(`Retrying after ${Math.round(backoffTime)}ms (attempt ${attempt + 1}/${retries})`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, backoffTime));
        attempt++;
      }
    }
    
    // This should never be reached due to the return in the catch block
    return { 
      data: null, 
      error: { message: 'Unknown error', code: 'UNKNOWN' } as PostgrestError,
      source: 'network' 
    };
  },
  
  /**
   * Wrapper for Supabase's from() method with standardized timeout
   */
  from: supabase.from.bind(supabase),
  
  /**
   * Wrapper for Supabase's auth property
   */
  auth: supabase.auth,
  
  /**
   * Wrapper for Supabase's storage property
   */
  storage: supabase.storage,
  
  /**
   * Wrapper for RPC calls with standardized timeout and retry handling
   * Uses the typedRpc client for proper type safety
   */
  rpc<F extends RpcFunctionName>(
    fn: F, 
    params: Record<string, any> = {}, 
    options?: { 
      timeout?: number; 
      retries?: number; 
      critical?: boolean;
      cacheKey?: string;
    }
  ) {
    // We need to modify our approach to use the query function properly
    return this.query(
      () => typedRpc.call(fn, params),
      options
    );
  }
};
